import { effect, inject, Injectable, Resource, resource, untracked } from '@angular/core';
import { SOFTLINE_CONFIG_STORED_CONTEXT_FIELD } from '../../utilities.shared';
import { Dictionary, IndexedDbService, isDefined } from '@softline/core';
import { UserInfoService } from '@softline/auth';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ContextStorageService implements Resource<object | null> {

  private readonly service = inject(IndexedDbService);
  private readonly fields = inject(SOFTLINE_CONFIG_STORED_CONTEXT_FIELD, { optional: true });
  private readonly userInfoService: UserInfoService<any> = inject(UserInfoService);

  private readonly resource = resource({
    params: () => ({ userInfo: this.userInfoService.value() }),
    loader: async () => {
      if (!this.userInfoService.value())
        return null;
      try {
        return firstValueFrom(this.service.get(
          { databaseName: 'context', objectStoreName: 'claims', key: this.userInfoService.value()?.userName }
        )) as object;
      } catch (e) {
        return null;
      }
    },
    defaultValue: null
  });

  constructor() {
    effect(() => {
      if (!this.fields || this.fields.length === 0)
        return;

      const userInfo = this.userInfoService.value();
      const username = userInfo?.userName;
      if (!userInfo)
        return;
      // merge onto the stored claims and skip undefined values: a token without a
      // vkforg claim must not wipe the remembered context that seeds the next login
      const stored = untracked(() => this.resource.value()) as Dictionary<unknown> | null;
      const base = stored?.['username'] === username ? stored : null;
      const claims: Dictionary<unknown> = { ...(base ?? {}), username };
      let changed = false;
      for (const field of this.fields) {
        const value = (userInfo as Dictionary<unknown>)[field];
        if (!isDefined(value) || claims[field] === value)
          continue;
        claims[field] = value;
        changed = true;
      }
      console.log(
        `[ContextStorage] token claims for '${username}'`,
        Object.fromEntries(this.fields.map((f) => [f, (userInfo as Dictionary<unknown>)[f]])),
        '— stored:', stored, changed ? '→ persisting' : '→ unchanged'
      );
      if (!changed)
        return;

      void firstValueFrom(
        this.service.update(
          { databaseName: 'context', objectStoreName: 'claims', key: username },
          claims
        )
      )
        .then(() => console.log(`[ContextStorage] persisted context for '${username}'`, claims))
        .catch((e) =>
          console.warn(`[ContextStorage] cannot persist context for '${username}'`, claims, e)
        );
      this.resource.set(claims);
    });
  }

  /**
   * Reads a user's stored context before authentication — `value` is gated on an
   * existing session and is therefore always null on the login page.
   */
  async loadFor(username: string): Promise<Dictionary<unknown> | null> {
    if (!username || !this.fields?.length)
      return null;
    try {
      const stored = (await firstValueFrom(
        this.service.get<Dictionary<unknown>>({
          databaseName: 'context',
          objectStoreName: 'claims',
          key: username,
        })
      )) as Dictionary<unknown> | null;
      const claims: Dictionary<unknown> = {};
      for (const field of this.fields) {
        if (isDefined(stored?.[field]))
          claims[field] = stored?.[field];
      }
      // the store is keyed by the userName the TOKEN carries — a differently
      // cased/typed login name reads an empty slot and seeds nothing
      console.log(`[ContextStorage] loadFor('${username}') — stored:`, stored ?? null,
        '→ seeding:', Object.keys(claims).length > 0 ? claims : null);
      return Object.keys(claims).length > 0 ? claims : null;
    } catch (e) {
      console.warn(`[ContextStorage] loadFor('${username}') failed`, e);
      return null;
    }
  }

  value = this.resource.value;
  status = this.resource.status;
  error = this.resource.error;
  isLoading = this.resource.isLoading;
  snapshot = this.resource.snapshot;
  hasValue(): this is Resource<object> {
    return this.value() !== null;
  }

}
